// ---------------------------------------------------------------------------
// Aggregation of contributions by outlet point — equivalent to
// F.acumula_apo() in Evalhid.dll: the real "Sal_Aportaciones.csv" doesn't
// have a column per sub-basin (that's this web's own export, diagnostic
// only), it has a column per OUTLET POINT, summing the volume of every
// sub-basin that drains into it (SubBasin.outletCode):
//
//   volume[t] = Σ basinArea(c) × contribution_c[t] × coefficient
//
// The real coefficient comes from APO_UNIDADES (a 3-row table — km²=0.001,
// ha=0.00001, "Other..." with a free name/value, see model/units.ts —
// selector in CalculationResults.tsx). The `coefficient` parameter of the
// two functions below is optional only for convenience when testing/calling
// without a project at hand; the real app always passes
// `activeUnitsCoefficient(project)` explicitly, never the default value
// silently.
//
// The real source also splits surface/groundwater runoff (esc_sup/esc_subt)
// by outlet point before summing them — confirmed model by model against
// the real source (Aportaciones.cs's acumula_apo, each model's own .cs), see
// modelRegistry.ts's SUP_SUBT_KEYS (moved there so engine.ts can precompute
// `SubBasinResult.aggregationDetail` from it without a circular import —
// this file reads that precomputed field below, not `detail` directly).
// The generic approximation (a model without a real engine) doesn't expose
// that split, and none is invented for it (this port's rule: replicate
// faithfully, don't approximate).
//
// Aggregation by ZONE (aggregateContributionsByZone) has no equivalent in the
// original (the .exe only aggregates by outlet point/aquifer) — it's a view
// of this web's own: zone is the only one of the 4 levels (sub-basin/outlet
// point/aquifer/zone) without its own geometry, so it's worth being able to
// see it aggregated too, not just as a visual grouping (grouping.ts). Same
// grouping criteria as groupBasinsByZone (zoneCode, the runoff zone —
// the snow one doesn't form a group here).
//
// Aggregation by AQUIFER (aggregateContributionsByAquifer) is Ac.calcula_acu()
// — each aquifer's own balance (Vo/Alfa), fed by the aggregated underground
// component of its linked sub-basins (see aquifer.ts). Its own discharge
// then flows back into aggregateContributionsByOutlet (see that function's
// own comment) — this isn't purely a 4th, independent view like Zone is; the
// outlet aggregation is genuinely incomplete without it whenever a project
// has real aquifers assigned.
// ---------------------------------------------------------------------------
import { calculateAquiferBalance } from "./aquifer.ts";
import { basinColumn, type SubBasinResult } from "./engine.ts";
import type { DailySeries } from "../model/dailySeries.ts";
import type { Aquifer, SubBasin, OutletPoint, Zone } from "../model/types.ts";
import { DEFAULT_UNIT_OPTIONS } from "../model/units.ts";

/** "km² → Hm³" (APO_UNIDADES code=0) — default value for the two functions below when none is passed explicitly. */
const DEFAULT_KM2_COEFFICIENT = DEFAULT_UNIT_OPTIONS[0].value;

export interface AggregatedContribution {
  /** Total aggregated volume — every sub-basin, whether or not a breakdown is available. */
  volume: Float64Array;
  /** Part of `volume` with a surface breakdown available (see SUP_SUBT_KEYS). */
  surfaceVolume: Float64Array;
  /** Part of `volume` with a groundwater breakdown available. */
  groundwaterVolume: Float64Array;
  /**
   * Part of `volume` from sub-basins WITHOUT a breakdown available (GR2M,
   * SAC, or the generic approximation) — neither surface nor groundwater,
   * unsplit. At every instant, `volume == surfaceVolume +
   * groundwaterVolume + noBreakdownVolume` (save for floating-point
   * rounding error).
   */
  noBreakdownVolume: Float64Array;
}

/**
 * Shared core: aggregates contribution (with sup/subt breakdown) of the
 * sub-basins whose `keyOf(subBasin)` matches each value of `keys`, in the
 * same order — `aggregated[i]` corresponds to `keys[i]`.
 */
function aggregateByKey(
  results: SubBasinResult[],
  subBasins: SubBasin[],
  keys: number[],
  keyOf: (subBasin: SubBasin) => number | null,
  coefficient: number
): AggregatedContribution[] {
  const subBasinByCode = new Map(subBasins.map((c) => [c.subBasinCode, c]));
  const stepCount = results[0]?.contribution.length ?? 0;

  // Bucketed once (O(sub-basins)) instead of re-scanning every result for
  // every key (O(keys × sub-basins)) — real gap found by deep-review
  // (2026-08-29): this runs 3 times per results-panel render (outlet/zone/
  // aquifer levels), each a full pass of every sub-basin for EVERY key.
  const resultsByKey = new Map<number, SubBasinResult[]>();
  for (const r of results) {
    const subBasin = subBasinByCode.get(r.subBasinCode);
    const key = subBasin ? keyOf(subBasin) : null;
    if (key === null) continue;
    const bucket = resultsByKey.get(key);
    if (bucket) bucket.push(r);
    else resultsByKey.set(key, [r]);
  }

  return keys.map((key) => {
    const volume = new Float64Array(stepCount);
    const surfaceVolume = new Float64Array(stepCount);
    const groundwaterVolume = new Float64Array(stepCount);
    const noBreakdownVolume = new Float64Array(stepCount);

    for (const r of resultsByKey.get(key) ?? []) {
      const subBasin = subBasinByCode.get(r.subBasinCode)!;
      const factor = subBasin.area * coefficient;

      for (let t = 0; t < stepCount; t++) {
        // NaN marks a step past a sub-basin's own truncation point
        // (engine.ts's padToFullLength, for a sub-basin whose precipitation/
        // ETP ran out partway through the calculated range) — treated as "no
        // contribution from THIS sub-basin at this step" rather than letting
        // it NaN-poison the whole aggregate for every OTHER sub-basin
        // draining into the same outlet/zone/aquifer at that same step.
        if (!Number.isFinite(r.contribution[t])) continue;
        volume[t] += r.contribution[t] * factor;
        if (r.aggregationDetail) {
          surfaceVolume[t] += r.aggregationDetail.surface[t] * factor;
          groundwaterVolume[t] += r.aggregationDetail.groundwater[t] * factor;
        } else {
          noBreakdownVolume[t] += r.contribution[t] * factor;
        }
      }
    }

    return { volume, surfaceVolume, groundwaterVolume, noBreakdownVolume };
  });
}

export interface OutletContribution extends AggregatedContribution {
  outletCode: number;
  name: string;
}

/**
 * Sums, for each outlet point, the contribution (already converted from mm
 * to volume via area × coefficient) of every calculated sub-basin that
 * drains into it, PLUS the discharge of every aquifer assigned to that same
 * outlet point (`aquiferContributions`, see aggregateContributionsByAquifer
 * below) — confirmed against the real source (`Aportaciones.cs:73-91`,
 * `acumula_apo`): a sub-basin's own contribution already excludes its
 * underground component when it has a real aquifer assigned (see each
 * model's own header); that water reappears here instead, via the
 * aquifer's own `discharge`, added to `volume` AND `groundwaterVolume`
 * (never `surfaceVolume`) of whichever outlet point the AQUIFER itself is
 * assigned to (`Aquifer.outletCode`) — which may or may not be the same
 * outlet point as the sub-basin's own. Omitting `aquiferContributions`
 * (or passing an empty array) reproduces the old, incomplete behavior —
 * every real caller should pass the real aggregation, computed once,
 * before this.
 *
 * Includes every `outletPoints` in the project, even if no calculated
 * sub-basin drains into one of them (it's left with volume 0 instead of
 * disappearing, same as the rest of this app's catalogs).
 */
export function aggregateContributionsByOutlet(
  results: SubBasinResult[],
  subBasins: SubBasin[],
  outletPoints: OutletPoint[],
  coefficient: number = DEFAULT_KM2_COEFFICIENT,
  aquiferContributions: AquiferContribution[] = []
): OutletContribution[] {
  const aggregated = aggregateByKey(
    results,
    subBasins,
    outletPoints.map((p) => p.outletCode),
    (c) => c.outletCode,
    coefficient
  );
  const outlets = outletPoints.map((p, i) => ({
    outletCode: p.outletCode,
    name: p.name,
    ...aggregated[i],
  }));

  const outletByCode = new Map(outlets.map((o) => [o.outletCode, o]));
  for (const aquifer of aquiferContributions) {
    if (aquifer.outletCode == null) continue;
    const outlet = outletByCode.get(aquifer.outletCode);
    if (!outlet) continue;
    for (let t = 0; t < outlet.volume.length; t++) {
      outlet.volume[t] += aquifer.discharge[t];
      outlet.groundwaterVolume[t] += aquifer.discharge[t];
    }
  }

  return outlets;
}

/** `Acuiferos.cs`'s own defaults (`Vo=0`, `Alfa=0.0001`) for an aquifer whose Vo/Alfa haven't been filled in (nullable in `Aquifer` — see types.ts) — the real .exe's own fallback, not one invented for this port. */
const DEFAULT_AQUIFER_VO = 0;
const DEFAULT_AQUIFER_ALFA = 0.0001;

export interface AquiferContribution {
  aquiferCode: number;
  name: string;
  /** Same "no outlet point assigned" sentinel as OutletPoint/SubBasin's own optional outletCode — see aggregateContributionsByOutlet's own use of it. */
  outletCode: number | null;
  /** Infiltration (volume) into the aquifer — Σ underground component × area × coefficient, from every sub-basin assigned to it (Acuiferos.cs's own "Infiltr", "_Rec" in Sal_Acuiferos.csv). */
  infiltration: Float64Array;
  /** Pumping (volume) actually applied — 0 at every step with no pumping series/column, or with "Modelar bombeos" unchecked (Acuiferos.cs's own "bomb", "_Bomb" in Sal_Acuiferos.csv). */
  pumping: Float64Array;
  /** Vf (volume): the aquifer's own storage after each step ("_Vol" in Sal_Acuiferos.csv, see aquifer.ts). */
  storage: Float64Array;
  /** Qs (volume): the aquifer's own discharge — its "aportación" ("_Qsal" in Sal_Acuiferos.csv), added into aggregateContributionsByOutlet above. */
  discharge: Float64Array;
}

/**
 * Runs each aquifer's own balance (aquifer.ts's `calculateAquiferBalance`),
 * fed by the aggregated underground component of every sub-basin assigned
 * to it (`SubBasin.aquiferCode`) — equivalent to `Ac.calcula_acu()` in the
 * real source, confirmed in the real (non-decompiled) C# source (2026-08,
 * see CLAUDE.md's engine-source audit): an earlier decompiled-DLL-based
 * analysis had concluded this was permanently dead (`Cuencas.inf`, the
 * field it seemed to sum, was never assigned by any model) — false with
 * the real source, where `Infiltr` is genuinely accumulated from each
 * sub-basin's own `esc_subt` and the balance genuinely recedes with its own
 * Vo/Alfa.
 *
 * `pumpingSeries`/`allowNegative` mirror `CalculationOptions.modelPumping`/
 * `negativeAquifers` ("Modelar bombeos"/"Negativos en acuíferos") — the
 * caller is responsible for already having gated `pumpingSeries` to `null`
 * when "Modelar bombeos" isn't checked (same convention `engine.ts` and
 * `useCalculation.ts`'s `seriesForCalculation` already use for every other
 * pumping series in this app — see calculationOptions.ts's own header for
 * why `Op_bombeos` is a real gate in the engine, not just a UI checkbox).
 */
export function aggregateContributionsByAquifer(
  results: SubBasinResult[],
  subBasins: SubBasin[],
  aquifers: Aquifer[],
  coefficient: number = DEFAULT_KM2_COEFFICIENT,
  pumpingSeries: DailySeries | null = null,
  allowNegative: boolean = false
): AquiferContribution[] {
  const aggregated = aggregateByKey(
    results,
    subBasins,
    aquifers.map((a) => a.aquiferCode),
    (c) => c.aquiferCode,
    coefficient
  );
  return aquifers.map((a, i) => {
    const infiltration = aggregated[i].groundwaterVolume;
    const pumpingSeriesForAquifer = pumpingSeries ? basinColumn(a.pumpingCol, pumpingSeries) : null;
    const { storage, discharge } = calculateAquiferBalance(
      infiltration,
      pumpingSeriesForAquifer,
      a.vo ?? DEFAULT_AQUIFER_VO,
      a.alfa ?? DEFAULT_AQUIFER_ALFA,
      allowNegative
    );
    const pumping = new Float64Array(infiltration.length);
    if (pumpingSeriesForAquifer) {
      for (let t = 0; t < pumping.length; t++) {
        pumping[t] = Number.isFinite(pumpingSeriesForAquifer[t]) ? pumpingSeriesForAquifer[t] : 0;
      }
    }
    return {
      aquiferCode: a.aquiferCode,
      name: a.name,
      outletCode: a.outletCode,
      infiltration,
      pumping,
      storage,
      discharge,
    };
  });
}

interface ZoneContribution extends AggregatedContribution {
  zoneCode: number;
  name: string;
}

/**
 * Aggregates by runoff zone (zoneCode) — same grouping criteria as
 * `groupBasinsByZone` (grouping.ts), but producing an aggregated volume
 * instead of just grouping rows. Only includes runoff zones
 * (`modelTypeCode != null`) — snow zones don't have their own
 * contribution to aggregate here.
 */
export function aggregateContributionsByZone(
  results: SubBasinResult[],
  subBasins: SubBasin[],
  zones: Zone[],
  coefficient: number = DEFAULT_KM2_COEFFICIENT
): ZoneContribution[] {
  const runoffZones = zones.filter((z) => z.modelTypeCode != null);
  const aggregated = aggregateByKey(
    results,
    subBasins,
    runoffZones.map((z) => z.zoneCode),
    (c) => c.zoneCode,
    coefficient
  );
  return runoffZones.map((z, i) => ({
    zoneCode: z.zoneCode,
    name: z.name,
    ...aggregated[i],
  }));
}
